import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganization } from '../contexts/OrganizationContext'
import { useMasterAdmin } from '../contexts/MasterAdminContext'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Textarea from '../components/Textarea'
import {
  User,
  Building2,
  Mail,
  AlertCircle,
  CheckCircle2,
  Clock,
  Plus,
  Search,
  X,
  FileText,
} from 'lucide-react'
import RequestOrganizationWizard from '../components/RequestOrganizationWizard'
import { createJoinRequest, getOrganizationJoinRequests } from '../services/onboarding'
import { formatDate } from '../utils/helpers'
import TermsAcceptanceModal from '../components/TermsAcceptanceModal'
import { useToast } from '../contexts/ToastContext'

const UserSetup = () => {
  const navigate = useNavigate()
  const { organization, loading, reload } = useOrganization()
  const { isMasterAdmin } = useMasterAdmin()
  const { success, error: showError } = useToast()
  const [activeTab, setActiveTab] = useState('overview')
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [joinRequests, setJoinRequests] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateOrg, setShowCreateOrg] = useState(false)
  const [showJoinRequest, setShowJoinRequest] = useState(false)
  const [orgName, setOrgName] = useState('')
  const [joinRequestData, setJoinRequestData] = useState({
    organizationId: '',
    message: '',
    requestedRole: 'member',
  })
  const [submitting, setSubmitting] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [loadingOrgs, setLoadingOrgs] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [newOrgId, setNewOrgId] = useState(null)
  const [userId, setUserId] = useState(null)
  const [showRequestWizard, setShowRequestWizard] = useState(false)
  const [userRequests, setUserRequests] = useState([])

  useEffect(() => {
    // Get user ID
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setUserId(user?.id)
    }
    getUser()
  }, [])

  useEffect(() => {
    // If user has organization, redirect to dashboard
    if (!loading && organization) {
      navigate('/dashboard')
    }
  }, [organization, loading, navigate])

  useEffect(() => {
    if (!loading && !organization) {
      fetchPendingInvitations()
      fetchJoinRequests()
      fetchUserRequests()
    }
  }, [loading, organization])

  const fetchUserRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.id) return

      const { data, error } = await supabase
        .from('organization_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setUserRequests(data || [])
    } catch (error) {
      console.error('Error fetching user requests:', error)
    }
  }

  const fetchPendingInvitations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.email) return

      const { data, error } = await supabase
        .from('organization_invitations')
        .select('*, organizations(name)')
        .eq('email', user.email)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })

      if (error) throw error
      setPendingInvitations(data || [])
    } catch (error) {
      console.error('Error fetching invitations:', error)
    }
  }

  const fetchJoinRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.id) return

      const { data, error } = await supabase
        .from('organization_join_requests')
        .select('*, organizations(name)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setJoinRequests(data || [])
    } catch (error) {
      console.error('Error fetching join requests:', error)
    }
  }

  const searchOrganizations = async () => {
    if (!searchTerm.trim()) {
      setOrganizations([])
      return
    }

    try {
      setLoadingOrgs(true)
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, slug, created_at')
        .ilike('name', `%${searchTerm}%`)
        .limit(10)

      if (error) throw error
      setOrganizations(data || [])
    } catch (error) {
      console.error('Error searching organizations:', error)
    } finally {
      setLoadingOrgs(false)
    }
  }

  const handleCreateOrganization = async () => {
    if (!orgName.trim()) return

    try {
      setSubmitting(true)
      
      // Use SECURITY DEFINER function to create organization (bypasses RLS)
      const orgSlug = `org-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

      // Check if email is in master admin system
      const { data: masterAdminCheck } = await supabase
        .from('master_admins')
        .select('email')
        .eq('email', (await supabase.auth.getUser()).data.user?.email?.toLowerCase() || '')
        .eq('is_active', true)
        .maybeSingle()
      
      // Assign tier: trial if master admin email, demo otherwise
      const tier = masterAdminCheck ? 'trial' : 'demo'
      
      const { data, error } = await supabase.rpc('create_organization', {
        org_name: orgName.trim(),
        org_slug: orgSlug,
        subscription_tier_param: tier
      })

      if (error) throw error

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create organization')
      }

      // Set new org ID and show terms modal
      setNewOrgId(data.organization_id)
      setShowTermsModal(true)
      
      // Reload organization context
      await reload()
      success('Organization created successfully!', {
        title: 'Success',
      })
    } catch (error) {
      console.error('Error creating organization:', error)
      showError(error.message || 'Failed to create organization. Please try again.', {
        title: 'Creation Failed',
      })
    } finally {
      setSubmitting(false)
      setShowCreateOrg(false)
      setOrgName('')
    }
  }

  const handleJoinRequest = async () => {
    if (!joinRequestData.organizationId) return

    try {
      setSubmitting(true)
      await createJoinRequest(
        joinRequestData.organizationId,
        joinRequestData.requestedRole,
        joinRequestData.message
      )

      setJoinRequestData({
        organizationId: '',
        message: '',
        requestedRole: 'member',
      })
      setShowJoinRequest(false)
      fetchJoinRequests()
      success('Your join request has been sent successfully. The organization admin will review your request.', {
        title: 'Request Sent',
        duration: 6000,
      })
    } catch (error) {
      console.error('Error creating join request:', error)
      showError(error.message || 'Failed to send join request. Please try again.', {
        title: 'Request Failed',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const acceptInvitation = async (token) => {
    try {
      const { data, error } = await supabase.rpc('accept_invitation', {
        invitation_token: token,
      })

      if (error) throw error

      if (data.success) {
        await reload()
        success('You have successfully joined the organization!', {
          title: 'Invitation Accepted',
        })
        setTimeout(() => navigate('/dashboard'), 1500)
      } else {
        const errorMessage = data.error || 'Failed to accept invitation'
        showError(errorMessage, {
          title: 'Invitation Failed',
        })
      }
    } catch (error) {
      console.error('Error accepting invitation:', error)
      showError(error.message || 'Failed to accept invitation. Please try again.', {
        title: 'Acceptance Failed',
      })
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Setting up your account...</p>
        </div>
      </div>
    )
  }

  if (organization) {
    return null // Will redirect via useEffect
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    // Hide invitations tab until Resend is set up - only show to master admins
    ...(isMasterAdmin ? [{ id: 'invitations', label: 'Invitations', icon: Mail }] : []),
    { id: 'join', label: 'Join Organization', icon: Building2 },
    { id: 'request', label: 'Request Organization', icon: Plus },
  ]

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-4xl"
      >
        <Card variant="glass" className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-primary-400 to-secondary-400 flex items-center justify-center shadow-lg"
            >
              <AlertCircle size={40} className="text-white" />
            </motion.div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              Organization Setup Required
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              You need to be part of an organization to access the application.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-b-2 border-primary-600 text-primary-700 dark:text-primary-400'
                      : 'text-gray-600 dark:text-gray-300 hover:text-primary-600 hover:border-primary-300'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="p-6 bg-blue-50 border border-blue-200 rounded-xl">
                    <h3 className="font-semibold text-blue-900 mb-2">What's Next?</h3>
                    <p className="text-sm text-blue-800 mb-4">
                      To use this application, you need to be part of an organization. You can:
                    </p>
                    <ul className="list-disc list-inside text-sm text-blue-800 space-y-2">
                      <li>Accept a pending invitation if you have one</li>
                      <li>Request to join an existing organization</li>
                      <li>Request to set up your own official wellness center or organization</li>
                    </ul>
                  </div>

                  {pendingInvitations.length > 0 && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                      <p className="text-sm font-semibold text-green-800">
                        You have {pendingInvitations.length} pending invitation(s)
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        Check the "Invitations" tab to accept them.
                      </p>
                    </div>
                  )}

                  {joinRequests.length > 0 && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                      <p className="text-sm font-semibold text-yellow-800">
                        You have {joinRequests.filter((r) => r.status === 'pending').length} pending join request(s)
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Waiting for organization admin approval.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Invitations Tab */}
              {activeTab === 'invitations' && (
                <div className="space-y-4">
                  {pendingInvitations.length === 0 ? (
                    <Card variant="soft" className="p-6 text-center">
                      <Mail size={32} className="mx-auto mb-2 text-gray-400 dark:text-gray-500" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No pending invitations</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        Ask your organization admin to send you an invitation.
                      </p>
                    </Card>
                  ) : (
                    pendingInvitations.map((invitation) => (
                      <Card key={invitation.id} variant="soft" className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                              {invitation.organizations?.name || 'Organization'}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                              Role: <span className="capitalize font-medium">{invitation.role}</span>
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              Invited: {formatDate(invitation.created_at)} • Expires:{' '}
                              {formatDate(invitation.expires_at)}
                            </p>
                          </div>
                          <Button onClick={() => acceptInvitation(invitation.token)}>
                            Accept Invitation
                          </Button>
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              )}

              {/* Join Organization Tab */}
              {activeTab === 'join' && (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Request to Join</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Search for an organization and send a join request. The admin will review your request.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search organization by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && searchOrganizations()}
                        className="flex-1"
                      />
                      <Button onClick={searchOrganizations} disabled={loadingOrgs}>
                        <Search size={18} className="mr-2" />
                        Search
                      </Button>
                    </div>
                  </div>

                  {organizations.length > 0 && (
                    <div className="space-y-2">
                      {organizations.map((org) => (
                        <Card key={org.id} variant="soft" className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100">{org.name}</h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Created {formatDate(org.created_at)}
                              </p>
                            </div>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setJoinRequestData({
                                  ...joinRequestData,
                                  organizationId: org.id,
                                })
                                setShowJoinRequest(true)
                              }}
                            >
                              Request to Join
                            </Button>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Join Requests History */}
                  {joinRequests.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Your Join Requests</h3>
                      <div className="space-y-2">
                        {joinRequests.map((request) => (
                          <Card key={request.id} variant="soft" className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                                  {request.organizations?.name || 'Organization'}
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Requested: {formatDate(request.created_at)}
                                </p>
                              </div>
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  request.status === 'approved'
                                    ? 'bg-green-100 text-green-700'
                                    : request.status === 'rejected'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}
                              >
                                {request.status}
                              </span>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Request Organization Tab */}
              {activeTab === 'request' && (
                <div className="space-y-4">
                  {!showRequestWizard ? (
                    <>
                      <div className="p-4 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                          <FileText size={20} />
                          Request Your Organization
                        </h3>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                          Ready to set up your own official wellness center or organization? 
                          Submit a request and our team will review it. We'll contact you to discuss 
                          your needs, understand your pain points, and help you get started.
                        </p>
                        <Button onClick={() => setShowRequestWizard(true)} className="w-full">
                          <Plus size={18} className="mr-2" />
                          Start Request Wizard
                        </Button>
                      </div>

                      {userRequests.length > 0 && (
                        <div className="mt-6">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Your Requests</h3>
                          <div className="space-y-2">
                            {userRequests.map((request) => (
                              <Card key={request.id} variant="soft" className="p-4">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">{request.requested_organization_name}</h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      Submitted: {formatDate(request.created_at)}
                                    </p>
                                  </div>
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                      request.status === 'approved'
                                        ? 'bg-green-100 text-green-700'
                                        : request.status === 'rejected'
                                        ? 'bg-red-100 text-red-700'
                                        : request.status === 'contacted'
                                        ? 'bg-blue-100 text-blue-700'
                                        : request.status === 'under_review'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {request.status.replace('_', ' ')}
                                  </span>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <RequestOrganizationWizard
                      onSuccess={(data) => {
                        setShowRequestWizard(false)
                        fetchUserRequests()
                      }}
                      onCancel={() => setShowRequestWizard(false)}
                    />
                  )}
                </div>
              )}

              {/* Create Organization Tab - Only visible to master admins (kept for backward compatibility) */}
              {activeTab === 'create' && isMasterAdmin && (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Create Organization</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      As a master administrator, you can create new organizations. The organization will be created with the specified tier.
                    </p>
                  </div>

                  {!showCreateOrg ? (
                    <Button onClick={() => setShowCreateOrg(true)} className="w-full">
                      <Plus size={18} className="mr-2" />
                      Create New Organization
                    </Button>
                  ) : (
                    <Card variant="soft" className="p-6">
                      <div className="space-y-4">
                        <Input
                          label="Organization Name"
                          placeholder="e.g. My Health Practice"
                          value={orgName}
                          onChange={(e) => setOrgName(e.target.value)}
                          required
                        />
                        <div className="flex gap-3">
                          <Button
                            onClick={handleCreateOrganization}
                            disabled={!orgName.trim() || submitting}
                            className="flex-1"
                          >
                            {submitting ? 'Creating...' : 'Create Organization'}
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setShowCreateOrg(false)
                              setOrgName('')
                            }}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Join Request Modal */}
          <AnimatePresence>
            {showJoinRequest && (
              <>
                <motion.div
                  className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowJoinRequest(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4"
                >
                  <Card variant="glass" className="w-full max-w-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Request to Join</h3>
                      <button
                        onClick={() => setShowJoinRequest(false)}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <X size={20} />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Requested Role
                        </label>
                        <select
                          value={joinRequestData.requestedRole}
                          onChange={(e) =>
                            setJoinRequestData({ ...joinRequestData, requestedRole: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="member">Member</option>
                          <option value="viewer">Viewer</option>
                          <option value="practitioner">Practitioner</option>
                        </select>
                      </div>
                      <Textarea
                        label="Message (Optional)"
                        placeholder="Tell the admin why you'd like to join..."
                        value={joinRequestData.message}
                        onChange={(e) =>
                          setJoinRequestData({ ...joinRequestData, message: e.target.value })
                        }
                        rows={3}
                      />
                      <div className="flex gap-3">
                        <Button
                          onClick={handleJoinRequest}
                          disabled={!joinRequestData.organizationId || submitting}
                          className="flex-1"
                        >
                          {submitting ? 'Sending...' : 'Send Request'}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => setShowJoinRequest(false)}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </Card>
      </motion.div>

      {/* Terms Acceptance Modal */}
      {showTermsModal && newOrgId && userId && (
        <TermsAcceptanceModal
          organizationId={newOrgId}
          userId={userId}
          onAccept={() => {
            setShowTermsModal(false)
            navigate('/dashboard')
          }}
        />
      )}
    </div>
  )
}

export default UserSetup

